import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BadgeCheck, ShieldAlert, ShieldQuestion } from "lucide-react";

const STATUS_COPY = {
  verified: { color: "text-gold-500", bg: "bg-gold-50 border-gold-200", label: "On file · valid" },
  expired: { color: "text-red-600", bg: "bg-red-50 border-red-200", label: "On file · EXPIRED" },
  pending: { color: "text-amber-600", bg: "bg-amber-50 border-amber-200", label: "On file · no expiry recorded" },
  unavailable: { color: "text-white/70", bg: "bg-[#0E0E0E] border-white/10", label: "Not currently active on SecuSource" },
  not_found: { color: "text-red-600", bg: "bg-red-50 border-red-200", label: "Not found on SecuSource" },
};

export default function BadgeVerify() {
  const [licence_type, setType] = useState("PSA");
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const check = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/public/verify-badge", { licence_type, number });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const key = !result ? null : (result.found ? result.status : "not_found");
  const copy = key ? STATUS_COPY[key] || STATUS_COPY.not_found : null;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-outfit font-bold text-white">Verify a PSA or SIA licence</h1>
        <p className="text-white/60 text-sm mt-2 font-manrope">Quick lookup before deployment. Confirms the licence number is on file with an active SecuSource pro — check the expiry against your job date.</p>
      </div>

      <form onSubmit={check} className="card-flat p-6 space-y-4">
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label>Licence type</Label>
            <select value={licence_type} onChange={(e) => setType(e.target.value)} className="w-full h-10 rounded-md border border-white/10 bg-[#0A0A0A] px-3 text-sm" data-testid="verify-type">
              <option value="PSA">PSA (Ireland)</option>
              <option value="SIA">SIA (UK)</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <Label>Licence number</Label>
            <Input required value={number} onChange={(e) => setNumber(e.target.value)} placeholder="e.g., PSA-DEMO-001" data-testid="verify-number" />
          </div>
        </div>
        <Button type="submit" disabled={busy} className="bg-brand hover:bg-brand-hover text-white" data-testid="verify-submit">
          {busy ? "Checking…" : "Check licence"}
        </Button>
      </form>

      {result && copy && (
        <div className={`card-flat p-6 border-l-4 ${copy.bg}`} data-testid="verify-result">
          <div className={`flex items-center gap-2 font-outfit font-bold ${copy.color}`}>
            {key === "verified" && <BadgeCheck className="w-5 h-5" />}
            {(key === "expired" || key === "not_found") && <ShieldAlert className="w-5 h-5" />}
            {(key === "pending" || key === "unavailable") && <ShieldQuestion className="w-5 h-5" />}
            {copy.label}
          </div>
          {result.found && (
            <div className="text-sm text-white/70 font-manrope mt-2 space-y-1">
              {result.secusource_id && <div>SecuSource ID: <span className="ss-id ml-1">{result.secusource_id}</span></div>}
              {result.expiry && <div>Expires: <span className="font-semibold text-white">{new Date(result.expiry).toLocaleDateString()}</span></div>}
              {result.country_of_residence && <div>Country: {result.country_of_residence}</div>}
              {result.reason && <div className="text-red-600">{result.reason}</div>}
            </div>
          )}
          {!result.found && (
            <div className="text-sm text-white/70 font-manrope mt-2">
              No approved SecuSource pro has this licence number on file. Ask the individual to register or contact SecuSource support.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
